/**
 * S5 (C3 reconciled build plan — docs/architecture/REALTIME-STREAMING.md):
 * health-gated staleTime for socket-fed queries (Sol Gap5).
 *
 * The socket owns freshness for positions/orders/pnl — but `staleTime:
 * Infinity` is only safe WHILE the live path is actually healthy:
 *   - this tab is the leader with an OPEN unified socket, or
 *   - this tab is a follower with a FRESH relay signal (data frame or the
 *     leader's 5s liveness heartbeat, S4).
 * Otherwise (WS disabled, socket down, relay stale) the queries fall back to
 * a finite safety TTL so react-query's focus/mount machinery re-fetches.
 *
 * NEVER `staleTime: 'static'` — it would block the S2 reconnect invalidation.
 *
 * Consumers pass `staleTime: socketFedStaleTime` (TanStack v5 accepts a
 * function, evaluated on every staleness check — the gate is always current).
 */

import { MARK_MAX_AGE_SECONDS } from "@/features/positions/utils/mark-staleness";
import { getLastQuotePulse } from "@/features/market-session/quote-pulse";
import { getLastRelaySignalAt, RELAY_FRESH_MS } from "./tab-relay-follower";

export { RELAY_FRESH_MS };

/** Finite safety TTL when the live path is degraded. */
export const SOCKET_FED_SAFETY_TTL_MS = 30_000;

let leaderSocketConnected = false;

/** Leader socket lifecycle (connection-unified onopen/onclose). */
export const markLeaderSocketConnected = (connected: boolean): void => {
  leaderSocketConnected = connected;
};

export const isStreamLive = (): boolean =>
  leaderSocketConnected || Date.now() - getLastRelaySignalAt() < RELAY_FRESH_MS;

/**
 * staleTime for socket-fed queries (positions/orders/pnl): Infinity while
 * live, finite safety TTL while degraded.
 */
export const socketFedStaleTime = (): number =>
  isStreamLive() ? Infinity : SOCKET_FED_SAFETY_TTL_MS;

/** Live-mode safety poll for position-truth queries (COPIER-DASHBOARD A1/A2). */
export const SOCKET_FED_LIVE_SAFETY_POLL_MS = 30_000;

/**
 * Degraded-mode poll base (ARCH S2). Degraded used to poll at 10 s — 3×
 * FASTER than live, fleet-synchronous: the moment the backend degraded,
 * every tab tripled its request rate at once (positive feedback on the
 * saturated core). Degraded now polls no faster than live; jitter (±33%)
 * desynchronizes the fleet. Base 45 s ± 33% keeps every tick inside
 * [30 s, 60 s]: never faster than live, never slower than the 60 s ceiling
 * that keeps the 2026-07-30 blank-book divergence class bounded.
 */
export const SOCKET_FED_DEGRADED_SAFETY_POLL_MS = 45_000;
export const SOCKET_FED_DEGRADED_JITTER = 1 / 3;

const jitterDegraded = (ms: number): number =>
  Math.round(
    ms *
      (1 -
        SOCKET_FED_DEGRADED_JITTER +
        Math.random() * 2 * SOCKET_FED_DEGRADED_JITTER),
  );

/**
 * refetchInterval for position-truth queries. An OPEN socket does not prove
 * the patch stream is complete: on 2026-07-30 the Positions book sat blank
 * while the Accounts card showed open P&L — `staleTime: Infinity` plus
 * `refetchInterval: false` meant nothing ever healed the divergence (the
 * dual-source lie). A slow REST heal caps silent divergence at one interval
 * while the socket keeps owning tick freshness; degraded tabs poll jittered
 * so a fleet-wide degrade never synchronizes a request storm.
 */
export const socketFedRefetchInterval = (): number =>
  isStreamLive()
    ? SOCKET_FED_LIVE_SAFETY_POLL_MS
    : jitterDegraded(SOCKET_FED_DEGRADED_SAFETY_POLL_MS);

/** Same 10s bound as backend MARK_MAX_AGE_SECONDS / FE quote expiry. */
export const QUOTE_STREAM_FRESH_MS = MARK_MAX_AGE_SECONDS * 1000;

/**
 * Tape-dark heal for the positions book. An OPEN unified socket is not
 * proof quotes are arriving — market_data.quote is cosmetic and the
 * Databento producer dies without closing the user socket. While the
 * socket is up but the last quote pulse is older than 10s, poll REST
 * faster than the 30s live safety tick so painted P&L cannot sit frozen.
 * Kept at 5s (not 2s) so a fleet-wide tape outage does not become a
 * request storm.
 */
export const SOCKET_FED_TAPE_DARK_POLL_MS = 5_000;

export const isQuoteStreamLive = (): boolean => {
  const pulseAt = getLastQuotePulse();
  return pulseAt != null && Date.now() - pulseAt < QUOTE_STREAM_FRESH_MS;
};

export const socketFedPnlStaleTime = (): number =>
  isStreamLive() && isQuoteStreamLive() ? Infinity : SOCKET_FED_SAFETY_TTL_MS;

export const socketFedPnlRefetchInterval = (): number => {
  if (isStreamLive() && isQuoteStreamLive()) {
    return SOCKET_FED_LIVE_SAFETY_POLL_MS;
  }
  if (isStreamLive()) {
    return SOCKET_FED_TAPE_DARK_POLL_MS;
  }
  return jitterDegraded(SOCKET_FED_DEGRADED_SAFETY_POLL_MS);
};
